import { useRef, useState } from 'react';
import { ApiError, NetworkError, api } from '../api';
import { useI18n } from '../i18n';
import { Field, Modal, Spinner } from '../components';
import { formatBytes } from '../money';

/**
 * Uploading an image to a patient's file.
 *
 * Not everything belongs to a tooth. A panoramic shows the whole jaw, a CBCT is
 * a volume, a consent form is a document — and the chart could only ever attach
 * an image to one tooth, so there was nowhere to put any of them. The tooth is
 * therefore OPTIONAL here, and the kinds that cannot sensibly belong to a single
 * tooth do not offer the field at all.
 *
 * Three things the server will check regardless of what this form says, because
 * an interface is not a control: the declared type must match the actual bytes,
 * the file must be within the size limit, and the kind must be one it knows.
 * They are checked here too so the person gets told before a slow upload rather
 * than after it.
 */

/** Kinds that describe a region rather than one tooth. */
const WHOLE_MOUTH = new Set(['panoramic', 'cephalometric', 'cbct', 'extraoral_photo', 'document', 'consent', 'report']);

const KINDS = [
  'panoramic',
  'cbct',
  'periapical',
  'bitewing',
  'cephalometric',
  'intraoral_photo',
  'extraoral_photo',
  'document',
  'consent',
  'report',
  'other',
] as const;

/**
 * Matches the server's default (`MAX_UPLOAD_BYTES`). Checked here only so a
 * person is told before a slow upload rather than after it — the server
 * enforces it regardless of what this form believes.
 */
const MAX_BYTES = 50 * 1024 * 1024;

const ACCEPT = '.jpg,.jpeg,.png,.webp,.pdf,.dcm,application/dicom,image/*';

export function ImagingUpload({
  patientId,
  defaultToothFdi = null,
  defaultZone = null,
  onClose,
  onUploaded,
}: {
  patientId: string;
  defaultToothFdi?: string | null;
  /** A face or body zone the photograph belongs to; hides the tooth field. */
  defaultZone?: string | null;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const { t, tx } = useI18n();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<string>(defaultZone ? 'extraoral_photo' : defaultToothFdi ? 'periapical' : 'panoramic');
  const [toothFdi, setToothFdi] = useState(defaultToothFdi ?? '');
  const [acquiredOn, setAcquiredOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wholeMouth = WHOLE_MOUTH.has(kind);
  const tooLarge = file !== null && file.size > MAX_BYTES;

  async function submit() {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const data = await readAsBase64(file);
      await api.uploadImage({
        patientId,
        kind,
        mimeType: file.type || guessMime(file.name),
        data,
        toothFdi: defaultZone || wholeMouth || !toothFdi.trim() ? null : toothFdi.trim(),
        zone: defaultZone ?? undefined,
        surfaces: [],
        note: note.trim() || undefined,
        // When the image was TAKEN, which is often not when it was uploaded —
        // a radiograph brought in from another practice is the obvious case.
        acquiredAt: `${acquiredOn}T12:00:00.000Z`,
      });
      onUploaded();
    } catch (e) {
      setError(
        e instanceof NetworkError ? t.errors.network : e instanceof ApiError ? e.message : t.common.error,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t.imaging.upload}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button type="button" className="btn" onClick={() => void submit()} disabled={busy || !file || tooLarge}>
            {busy ? (
              <>
                <Spinner /> {t.imaging.uploading}
              </>
            ) : (
              t.imaging.upload
            )}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}

        <Field label={t.imaging.file} required>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setError(null);
            }}
          />
        </Field>

        {file ? (
          <div className={tooLarge ? 'notice danger' : 'notice'}>
            <span>
              {file.name} · {formatBytes(file.size)}
              {tooLarge ? ` — ${tx(t.imaging.tooLarge, { limit: formatBytes(MAX_BYTES) })}` : ''}
            </span>
          </div>
        ) : null}

        <div className="grid-2">
          <Field label={t.imaging.kind} required>
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {(t.imaging.kinds as Record<string, string>)[k] ?? k}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t.imaging.acquiredOn} hint={t.imaging.acquiredOnHint}>
            <input
              type="date"
              value={acquiredOn}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setAcquiredOn(e.target.value)}
              className="ltr-value"
            />
          </Field>
        </div>

        {/* A panoramic belongs to the whole mouth; offering a tooth field for
            one would invite an answer that is not true. */}
        {defaultZone ? (
          <div className="notice">
            <span>{(t.zones.zone as Record<string, string>)[defaultZone] ?? defaultZone}</span>
          </div>
        ) : wholeMouth ? (
          <p className="hint" style={{ color: 'var(--ink-3)', fontSize: 12, margin: 0 }}>
            {t.imaging.wholeMouthNote}
          </p>
        ) : (
          <Field label={t.chart.tooth} hint={t.patients.optional}>
            <input
              value={toothFdi}
              onChange={(e) => setToothFdi(e.target.value.replace(/\D/g, '').slice(0, 2))}
              inputMode="numeric"
              className="ltr-value"
              placeholder="36"
            />
          </Field>
        )}

        <Field label={t.patients.notes} hint={t.patients.optional}>
          <input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>

        {kind === 'cbct' ? (
          <div className="notice warn">
            <span>{t.imaging.dicomNote}</span>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * The file as base64, without the `data:` prefix the API does not want.
 *
 * FileReader rather than a manual loop over the bytes: a CBCT is tens of
 * megabytes and building the string by hand blocks the page while it runs.
 */
function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

/** Some browsers report an empty type for .dcm; the extension is the fallback. */
function guessMime(name: string): string {
  const ext = name.toLowerCase().split('.').pop() ?? '';
  if (ext === 'dcm') return 'application/dicom';
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'png') return 'image/png';
  if (ext === 'webp') return 'image/webp';
  return 'image/jpeg';
}
